import { useCallback, useEffect, useMemo, useState } from "react";
import type { Locale } from "../../shared/dto";
import {
  adminOpsRunOrder,
  adminOpsSummary,
  dfyRunPhase,
  type AdminOpsCallDto,
  type AdminOpsResponse,
  type AdminOpsRestartRefusal,
  type AdminOpsRestartResult,
  type AdminOpsRunDto,
  type AdminOpsRunPhase,
  type AdminOpsSummary
} from "../../shared/admin-ops";
import { AdminReload } from "./AdminReload";
import { OpenClientLink } from "./OpenClient";
import { formatDate } from "./core";
import { GATE_STEPS, dfyStepLabel, mergeDfySteps, type DfyConsoleGate } from "./DfyBuild";
import { TestFilterNote } from "./TestFilterNote";
import { isOfflineMutation, staffMutation } from "./model";
import "./admin-ops.css";

/** How often the home re-reads the world while the admin watches it. */
export const ADMIN_OPS_POLL_MS = 60_000;
/** The clock the elapsed and silence counters are drawn from. */
export const ADMIN_OPS_TICK_MS = 30_000;

export type AdminOpsRestartState =
  | { phase: "idle" }
  | { phase: "armed"; runId: string }
  | { phase: "busy"; runId: string }
  | { phase: "done"; runId: string; result: AdminOpsRestartResult }
  | { phase: "error"; runId: string; refusal: AdminOpsRestartRefusal | "unknown" | "offline" };

interface AdminOpsProps {
  t: (key: string) => string;
  locale: Locale;
  csrfToken: string;
  hideTestTenants: boolean;
  onHideTestTenantsChange?: (hide: boolean) => void;
  onOpenClient?: (clientId: string) => void;
}

export function adminOpsVisibleRuns(runs: readonly AdminOpsRunDto[], hideTest: boolean): AdminOpsRunDto[] {
  return hideTest ? runs.filter((run) => !run.isTest) : [...runs];
}

export function adminOpsVisibleCalls(
  calls: readonly AdminOpsCallDto[],
  nowMs: number
): AdminOpsCallDto[] {
  return calls
    .filter((call) => !call.cancelledAt || Date.parse(call.startsAt) >= nowMs)
    .sort((left, right) => Date.parse(left.startsAt) - Date.parse(right.startsAt));
}

export function minutesSince(value: string, nowMs: number): number {
  const parsed = Date.parse(value);
  if (!Number.isFinite(parsed)) return 0;
  return Math.max(0, Math.floor((nowMs - parsed) / 60_000));
}

/** One phase, one sentence. The copy is what tells the operator whether the
 * build is alive, and it must never read as "working" when it is not. */
export function adminOpsPhaseKey(phase: AdminOpsRunPhase): string {
  return `staffConsole.adminOps.phase.${phase}`;
}

// S2-01: a restart that never reached the Worker is not a refusal, it is a
// lost connection, and only that one is the operator's to fix.
export function adminOpsRefusalKey(refusal: AdminOpsRestartRefusal | "unknown" | "offline"): string {
  return `staffConsole.adminOps.refusal.${refusal}`;
}

export function AdminOps(props: AdminOpsProps) {
  const [data, setData] = useState<AdminOpsResponse | null>(null);
  const [listState, setListState] = useState<"loading" | "ready" | "error">("loading");
  const [restart, setRestart] = useState<AdminOpsRestartState>({ phase: "idle" });
  const [now, setNow] = useState(() => Date.now());

  const load = useCallback(async (options: { background?: boolean } = {}) => {
    if (!options.background) setListState("loading");
    try {
      const response = await fetch("/api/admin/ops", { credentials: "include" });
      const body = await response.json() as AdminOpsResponse & { ok?: boolean };
      if (!response.ok || !body.ok || !Array.isArray(body.runs)) throw new Error("admin_ops_load_failed");
      setData(body);
      setNow(Date.now());
      setListState("ready");
    } catch {
      setListState("error");
    }
  }, []);

  useEffect(() => { void load(); }, [load]);
  useEffect(() => {
    const timer = window.setInterval(() => { void load({ background: true }); }, ADMIN_OPS_POLL_MS);
    const onVisible = () => { if (!document.hidden) void load({ background: true }); };
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      window.clearInterval(timer);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [load]);
  useEffect(() => {
    const tick = window.setInterval(() => setNow(Date.now()), ADMIN_OPS_TICK_MS);
    return () => window.clearInterval(tick);
  }, []);

  const runs = useMemo(
    () => adminOpsVisibleRuns(data?.runs ?? [], props.hideTestTenants)
      .sort((left, right) => adminOpsRunOrder(left, right, now)),
    [data, now, props.hideTestTenants]
  );
  const recent = useMemo(
    () => adminOpsVisibleRuns(data?.recent ?? [], props.hideTestTenants),
    [data, props.hideTestTenants]
  );
  const calls = useMemo(() => adminOpsVisibleCalls(data?.calls ?? [], now), [data, now]);
  // Counted from the rows the operator can actually see, with the SAME
  // function the server counts with: hiding test tenants may change the
  // number, never the rule behind it.
  const summary = useMemo(() => adminOpsSummary(runs, now), [now, runs]);
  // Provoz counted from the visible rows already; what it never said is how
  // many rows the global filter took away (audit S-22).
  //
  // S2-06: one number for two lists, printed above the first of them, was a
  // third lie on top of the two it fixed. "Zobrazeno záznamů: 4. Skryto
  // testovacích: 1." stood over "Výroby, které někdo hlídá" while it summed
  // the running builds AND the finished ones, and the hidden record was a
  // build that had been running for three and a half days. Each list counts
  // itself now, under its own heading.
  const hiddenRuns = useMemo(() => (data?.runs.length ?? 0) - runs.length, [data, runs.length]);
  const hiddenRecent = useMemo(() => (data?.recent.length ?? 0) - recent.length, [data, recent.length]);

  // S2-17: a reload that failed left the counters, the running build and the
  // finished list on screen with nothing to say they were old, in a panel
  // whose entire promise is "what is happening RIGHT NOW". An operator could
  // restart a build that had finished in the meantime. The data stays (losing
  // it would be worse than knowing it is old) but it is MARKED, and the one
  // reload button lives in the header. A restart fired on a stale card is
  // still fenced by the server's optimistic check on `observedUpdatedAt`,
  // which answers `run_moved` instead of doubling the work.
  const stale = listState === "error" && data !== null;

  async function confirmRestart(run: AdminOpsRunDto) {
    setRestart({ phase: "busy", runId: run.runId });
    const response = await staffMutation(`/api/admin/ops/runs/${run.runId}/restart`, {
      method: "POST",
      csrfToken: props.csrfToken,
      body: { observedUpdatedAt: run.updatedAt }
    });
    const body = await response.json().catch(() => null) as
      | (AdminOpsRestartResult & { reason?: AdminOpsRestartRefusal })
      | { ok: false; reason?: AdminOpsRestartRefusal }
      | null;
    if (!response.ok || !body || body.ok !== true) {
      const refusal = isOfflineMutation(response)
        ? "offline" as const
        : body && "reason" in body && body.reason ? body.reason : "unknown";
      setRestart({ phase: "error", runId: run.runId, refusal });
      await load({ background: true });
      return;
    }
    setRestart({ phase: "done", runId: run.runId, result: body });
    await load({ background: true });
  }

  return (
    // F138d: the panel's SUMMARY carries the kicker, the name and the sentence
    // now (the S2-13 anatomy, one floor up), so repeating them here would be
    // the same panel introducing itself twice on one screen. The reload button
    // is not a heading and stays exactly where Servis keeps it.
    <section className="admin-ops">
      <header className="admin-ops-head">
        <AdminReload busy={listState === "loading"} onClick={() => void load()} t={props.t} />
      </header>

      <AdminOpsSummaryChips summary={summary} t={props.t} />

      {listState === "loading" && !data && <p>{props.t("common.loading")}</p>}
      {/* One reload button per panel, in the header where Servis keeps it
          (S2-17). The error used to carry a second copy of the same control
          190 px away, in a different size and with the same word on it. */}
      {listState === "error" && (
        <div className="admin-ops-load-error" role="alert">
          <p className="error-message">{props.t("staffConsole.adminOps.error.load")}</p>
          <p className="admin-tool-hint">
            {props.t(stale ? "staffConsole.adminOps.error.stale" : "staffConsole.adminOps.error.retry")}
          </p>
        </div>
      )}

      {data && (
        <div className={stale ? "admin-ops-data is-stale" : "admin-ops-data"} aria-busy={stale}>
          {!data.restartAvailable && (
            <p className="message warning-message" role="status">
              {props.t("staffConsole.adminOps.triggerOff")}
            </p>
          )}
          <section className="admin-ops-block" aria-labelledby="admin-ops-runs-title">
            <h4 id="admin-ops-runs-title">{props.t("staffConsole.adminOps.runs.title")}</h4>
            <TestFilterNote shown={runs.length} hidden={hiddenRuns} t={props.t}
              onShowTest={props.onHideTestTenantsChange && (() => props.onHideTestTenantsChange?.(false))} />
            {/* S2-06: "Výroba je čistá" is an absolute claim, and the filter
                had a build stalled for three and a half days behind it. The
                empty state speaks about what is VISIBLE and, when the filter
                is holding something back, sends the reader to it. */}
            {!runs.length && (
              <p className={hiddenRuns > 0 ? "staff-empty" : "staff-empty is-clear"}>
                {props.t(hiddenRuns > 0
                  ? "staffConsole.adminOps.runs.emptyFiltered"
                  : "staffConsole.adminOps.runs.empty")}
              </p>
            )}
            <div className="admin-ops-run-list">
              {runs.map((run) => (
                <AdminOpsRunCard
                  key={run.runId}
                  run={run}
                  now={now}
                  locale={props.locale}
                  t={props.t}
                  restart={restart}
                  restartAvailable={data.restartAvailable}
                  onArm={() => setRestart({ phase: "armed", runId: run.runId })}
                  onCancel={() => setRestart({ phase: "idle" })}
                  onConfirm={() => void confirmRestart(run)}
                  onOpenClient={props.onOpenClient}
                />
              ))}
            </div>
          </section>

          <AdminOpsRecent recent={recent} hidden={hiddenRecent} locale={props.locale} t={props.t}
            onShowTest={props.onHideTestTenantsChange && (() => props.onHideTestTenantsChange?.(false))}
            onOpenClient={props.onOpenClient} />
          <AdminOpsCalls calls={calls} locale={props.locale} t={props.t} />
        </div>
      )}
    </section>
  );
}

export function AdminOpsSummaryChips(props: { summary: AdminOpsSummary; t: (key: string) => string }) {
  const entries = [
    { key: "running", value: props.summary.running },
    { key: "stalled", value: props.summary.stalled },
    { key: "failed", value: props.summary.failed },
    { key: "awaiting", value: props.summary.awaiting }
  ];
  return (
    <ul className="admin-ops-summary">
      {entries.map((entry) => (
        <li key={entry.key} data-ops-metric={entry.key} data-ops-value={entry.value}>
          <strong>{entry.value}</strong>
          <span>{props.t(`staffConsole.adminOps.summary.${entry.key}`)}</span>
        </li>
      ))}
    </ul>
  );
}

export function AdminOpsRunCard(props: {
  run: AdminOpsRunDto;
  now: number;
  locale: Locale;
  t: (key: string) => string;
  restart: AdminOpsRestartState;
  restartAvailable: boolean;
  onArm: () => void;
  onCancel: () => void;
  onConfirm: () => void;
  onOpenClient?: (clientId: string) => void;
}) {
  const { run, t } = props;
  const phase = dfyRunPhase(run, props.now);
  const mine = props.restart.phase !== "idle" && props.restart.runId === run.runId;
  const armed = mine && props.restart.phase === "armed";
  const busy = mine && props.restart.phase === "busy";
  const material = t(`partnerOS.material.${run.gateKey}`);
  const catalog = consoleGateFor(run.gateKey);
  const steps = mergeDfySteps(
    catalog ? GATE_STEPS[catalog] : [],
    run.steps.map((step) => step.step)
  );
  const reported = new Map(run.steps.map((step) => [step.step, step.ok]));
  const current = run.steps.find((step) => !step.ok)?.step
    ?? steps.find((step) => reported.get(step) !== true)
    ?? null;
  const restartEnabled = run.canRestart && props.restartAvailable && !busy;

  return (
    <article className={`admin-ops-run is-${phase}`} data-ops-run={run.runId} data-ops-phase={phase}>
      <header className="admin-ops-run-head">
        <div>
          <strong>{run.clientName}</strong>
          <span className="admin-ops-run-material">{material}</span>
        </div>
        <span className="status-chip" data-ops-chip={phase}>{t(adminOpsPhaseKey(phase))}</span>
      </header>

      <p className="admin-ops-run-meta">
        <span>{t("staffConsole.dfyBuild.elapsed").replace("{minutes}", String(minutesSince(run.startedAt, props.now)))}</span>
        <span>{t("staffConsole.dfyBuild.lastActivity").replace("{minutes}", String(minutesSince(run.updatedAt, props.now)))}</span>
        {run.etaAt && <span>{t("staffConsole.adminOps.eta").replace("{time}", formatDate(run.etaAt, props.locale))}</span>}
      </p>

      {run.note && <p className="admin-ops-run-note">{run.note}</p>}
      {phase === "awaiting_restart" && run.restartRequestedAt && (
        <p className="message admin-ops-run-wait" role="status">
          {t("staffConsole.adminOps.restart.waiting").replace("{time}", formatDate(run.restartRequestedAt, props.locale))}
        </p>
      )}
      {phase === "restart_silent" && (
        <p className="message warning-message admin-ops-run-wait" role="alert">
          {t("staffConsole.adminOps.restart.silent")}
        </p>
      )}

      {steps.length > 0 && (
        <ol className="admin-ops-run-steps">
          {steps.map((step) => {
            const done = reported.get(step) === true;
            const active = !done && step === current && phase === "running";
            return (
              <li className={done ? "is-done" : active ? "is-current" : "is-remaining"} key={step}>
                <span aria-hidden="true">{done ? "✓" : active ? "●" : "○"}</span>
                {dfyStepLabel(step, t)}
              </li>
            );
          })}
        </ol>
      )}

      <div className="admin-ops-run-actions">
        {/* S3-07: one control for this act everywhere in Servis. */}
        <OpenClientLink clientId={run.clientId} onOpenClient={props.onOpenClient}
          label={t("staffConsole.adminOps.openClient")} />
        {!armed && !busy && (
          <button className="secondary-button" type="button" disabled={!restartEnabled}
            title={restartEnabled ? undefined : t(restartBlockTitleKey(run, props.restartAvailable))}
            onClick={props.onArm}>
            {t("staffConsole.adminOps.restart.start")}
          </button>
        )}
        {armed && (
          <div className="admin-ops-restart-confirm" role="group"
            aria-label={t("staffConsole.adminOps.restart.confirmTitle")}>
            <p>{t("staffConsole.adminOps.restart.confirmBody")
              .replace("{client}", run.clientName)
              .replace("{material}", material)}</p>
            <button className="primary-button" type="button" onClick={props.onConfirm}>
              {t("staffConsole.adminOps.restart.confirm")}
            </button>
            <button className="text-button" type="button" onClick={props.onCancel}>
              {t("common.cancel")}
            </button>
          </div>
        )}
      </div>

      <div className="admin-ops-run-feedback" aria-live="polite">
        {busy && <p>{t("staffConsole.adminOps.restart.working")}</p>}
        {mine && props.restart.phase === "done" && (
          <AdminOpsRestartCard result={props.restart.result} locale={props.locale} t={t}
            onOpenClient={props.onOpenClient} />
        )}
        {mine && props.restart.phase === "error" && (
          <div className="admin-ops-restart-error" role="alert">
            <p className="error-message">{t(adminOpsRefusalKey(props.restart.refusal))}</p>
            <p>{t("staffConsole.adminOps.restart.retryHint")}</p>
          </div>
        )}
      </div>
    </article>
  );
}

/** Which step catalog to DRAW for a reported run key. Presentation only: the
 * strategy runner reports under "strategy" and "plan", both of which the
 * console shows with the audit checklist. An unknown key draws only what the
 * runner actually reported instead of inventing a checklist. */
export function consoleGateFor(gateKey: string): DfyConsoleGate | null {
  if (gateKey === "strategy" || gateKey === "plan" || gateKey === "audit") return "audit";
  if (gateKey === "salespage" || gateKey === "emails" || gateKey === "webinar") return gateKey;
  return null;
}

function restartBlockTitleKey(run: AdminOpsRunDto, restartAvailable: boolean): string {
  if (!restartAvailable) return "staffConsole.adminOps.triggerOff";
  return `staffConsole.adminOps.blocked.${run.restartBlockedBy ?? "finished"}`;
}

/** Hotovo names the object, its new state and the next step. A restart that
 * the server refused to duplicate says exactly WHY it changed nothing. */
export function AdminOpsRestartCard(props: {
  result: AdminOpsRestartResult;
  locale: Locale;
  t: (key: string) => string;
  onOpenClient?: (clientId: string) => void;
}) {
  const { result, t } = props;
  const material = t(`partnerOS.material.${result.gateKey}`);
  const headline = result.outcome === "launched"
    ? t("staffConsole.adminOps.restart.launched")
    : t(`staffConsole.adminOps.restart.skipped.${result.reason ?? "run_active"}`);
  return (
    <section className="admin-ops-restart-card" role="status" data-ops-outcome={result.outcome}>
      <p className="panel-label">{t("staffConsole.adminOps.restart.resultKicker")}</p>
      <h5>{`${result.clientName} · ${material}`}</h5>
      <p>{headline}</p>
      <p className="admin-ops-restart-time">
        {t("staffConsole.adminOps.restart.at").replace("{time}", formatDate(result.requestedAt, props.locale))}
      </p>
      {result.outcome === "launched" && <p>{t("staffConsole.adminOps.restart.nextStep")}</p>}
      <OpenClientLink clientId={result.clientId} onOpenClient={props.onOpenClient}
        label={t("staffConsole.adminOps.openClient")} />
    </section>
  );
}

export function AdminOpsRecent(props: {
  recent: readonly AdminOpsRunDto[];
  /** Rows of THIS list the global filter removed (audit S2-06). */
  hidden?: number;
  locale: Locale;
  t: (key: string) => string;
  onShowTest?: () => void;
  onOpenClient?: (clientId: string) => void;
}) {
  return (
    <section className="admin-ops-block" aria-labelledby="admin-ops-recent-title">
      <h4 id="admin-ops-recent-title">{props.t("staffConsole.adminOps.recent.title")}</h4>
      <TestFilterNote shown={props.recent.length} hidden={props.hidden ?? 0} t={props.t}
        onShowTest={props.onShowTest} />
      {!props.recent.length && <p className="staff-empty">{props.t("staffConsole.adminOps.recent.empty")}</p>}
      {props.recent.length > 0 && (
        <ul className="admin-ops-recent-list">
          {props.recent.map((run) => (
            <li key={run.runId} data-ops-recent={run.runId}>
              <strong>{run.clientName}</strong>
              <span>{props.t(`partnerOS.material.${run.gateKey}`)}</span>
              <time dateTime={run.finishedAt ?? run.updatedAt}>
                {formatDate(run.finishedAt ?? run.updatedAt, props.locale)}
              </time>
              <OpenClientLink clientId={run.clientId} onOpenClient={props.onOpenClient}
                label={props.t("staffConsole.adminOps.openClient")} />
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

export function AdminOpsCalls(props: {
  calls: readonly AdminOpsCallDto[];
  locale: Locale;
  t: (key: string) => string;
}) {
  return (
    <section className="admin-ops-block" aria-labelledby="admin-ops-calls-title">
      <h4 id="admin-ops-calls-title">{props.t("staffConsole.adminOps.calls.title")}</h4>
      <p className="admin-ops-block-hint">{props.t("staffConsole.adminOps.calls.hint")}</p>
      {!props.calls.length && <p className="staff-empty">{props.t("staffConsole.adminOps.calls.empty")}</p>}
      {props.calls.length > 0 && (
        <ul className="admin-ops-call-list">
          {props.calls.map((call) => (
            <li key={call.id} data-ops-call={call.id} className={call.cancelledAt ? "is-cancelled" : undefined}>
              <time dateTime={call.startsAt}>{formatDate(call.startsAt, props.locale)}</time>
              <span>{props.t(`staffConsole.callKindValue.${call.kind}`)}</span>
              <span>{`${call.enrolledCount}/${call.capacity}`}</span>
              {call.cancelledAt
                ? <em>{props.t("staffConsole.adminOps.calls.cancelled")}</em>
                : <a href={call.zoomUrl} target="_blank" rel="noreferrer">{props.t("staffConsole.adminOps.calls.open")}</a>}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
